import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { BookDemoButton } from "./BookDemoButton";
import { IMG } from "../lib/scenes";

const EASE = [0.16, 1, 0.3, 1];

const LINES = [
  { text: "One Kitchen.", accent: false },
  { text: "One System.", accent: false },
  { text: "One Way to Work.", accent: true },
];

const MaskLine = ({ children, delay, accent }) => (
  <span className="mo-line-mask">
    <motion.span
      className={`mo-line-inner ${accent ? "text-[#EA6A43]" : "text-white"}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const bgScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.16]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.5, 0.8], [1, 1, 0]);

  return (
    <section id="hero" data-scene ref={ref} className="relative h-[150vh]">
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        <motion.div style={{ scale: bgScale, y: bgY }} className="absolute inset-0">
          <motion.img
            src={IMG.hero}
            alt="A chef in the kitchen, in the calm before service"
            className="h-full w-full object-cover"
            style={{ filter: "brightness(1.42) contrast(1.02)", objectPosition: "72% center" }}
            initial={{ opacity: 0, filter: "brightness(1.42) contrast(1.02) blur(14px)" }}
            animate={{ opacity: 1, filter: "brightness(1.42) contrast(1.02) blur(0px)" }}
            transition={{ duration: 1.8, ease: EASE }}
            loading="eager"
            fetchPriority="high"
          />
          {/* contrast scrim for legibility, focused low + left */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#141210]/72 via-[#141210]/14 to-[#141210]/6" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#141210]/70 via-[#141210]/24 to-transparent" />
          {/* soft top vignette so nav logo stays legible over any bright area */}
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#141210]/45 to-transparent" />
          {/* melt into the canvas below */}
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#f5f2ea]" />
        </motion.div>

        <motion.div
          style={{ opacity: contentOpacity }}
          className="relative z-10 flex h-full w-full flex-col justify-center px-6 md:px-12 lg:px-24"
        >
          <div className="mx-auto w-full max-w-[1600px]">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="mb-9 flex items-center gap-4"
            >
              <span className="h-px w-12 bg-[#EA6A43]" />
              <span className="font-body text-[11px] uppercase tracking-[0.34em] text-white/90">
                Before service
              </span>
            </motion.div>

            <h1 className="font-display text-[14vw] font-black leading-[0.9] tracking-tighter sm:text-[12vw] lg:text-[8.4vw]">
              {LINES.map((l, i) => (
                <MaskLine key={i} delay={0.5 + i * 0.16} accent={l.accent}>
                  {l.text}
                </MaskLine>
              ))}
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.9, ease: EASE }}
              className="mt-12 flex flex-col items-start gap-8 sm:flex-row sm:items-center"
            >
              <BookDemoButton size="lg" testId="hero-book-demo" />
              <p className="font-body text-base font-semibold tracking-wide text-white drop-shadow-[0_1px_10px_rgba(0,0,0,0.55)]">
                The Digital Mise en Place.
              </p>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          style={{ opacity: contentOpacity }}
          className="absolute bottom-10 left-1/2 z-10 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
            className="flex flex-col items-center gap-2 text-white/75"
          >
            <span className="font-body text-[10px] uppercase tracking-[0.3em]">The day begins</span>
            <ArrowDown size={16} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
